import React, { useState, useEffect } from 'react';
import BlogService from '../../services/BlogServices';
import { toast } from 'react-toastify';
import PaginationComponent from '../common/PaginationComponent';
import UpdateSchemaModal from '../formModal/UpdateSchemaModal';

const BlogTable = ({ websiteId, loginUser, setSelectedBlog}) => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalBlogs, setTotalBlogs] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [size, setSize] = useState(10);
    const [showSchemaModal, setShowSchemaModal] = useState(false);
    const [blog, setBlog] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedBlogId, setSelectedBlogId] = useState(null);  

    const [coverImageAlt, setCoverImageAlt] = useState("");
    const [showImageAltModal, setShowImageAltModal] = useState(false);

    const [slug, setSlug] = useState("");
    const [showSlugModal, setShowSlugModal] = useState(false);

    // Fetch blogs when the websiteId changes
    useEffect(() => {
        if (websiteId) {
            fetchBlogs();
        }
    }, [websiteId, currentPage, size]);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const response = await BlogService.getPaginatedBlogs(currentPage, size, websiteId); // Replace with your API endpoint
            setBlogs(response.content || []);
            setTotalPages(response.totalPages);
            setTotalBlogs(response.totalElements);
        } catch (error) {
            toast.error(error.message || 'Failed to fetch Blogs.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (blogId) => {
        const confirmAction = window.confirm(`Are you sure you want to Delete the Blog?`);
         if (!confirmAction) {
            return;
        }
        try {
            await BlogService.deleteBlog(websiteId, blogId);
            toast.info('Blog Deleted successfully.');
            fetchBlogs();
        } catch (error) {
            toast.error(error.message || 'Failed to delete user.');
        }
    };

    const handleChangePublishedDate = async () => {
        if (!selectedDate) {
            toast.error("Please select a date.");
            return;
        }
        try {
            const formattedDate = new Date(selectedDate).toISOString(); // Ensures proper formatting
            await BlogService.changePublishedDate(websiteId, selectedBlogId, formattedDate);
            toast.success('Published date updated successfully.');
            dateModalClose(false);
            fetchBlogs();
        } catch (error) {
            toast.error(error.message || 'Failed to update published date.');
        }
    };

    const handlCoverImageAlt = async () => {
        if (!coverImageAlt) {
            toast.error("Alt tag must not be null or empty.");
            return;
        }
        if (coverImageAlt.length > 255) {
            toast.error("Alt tag must not exceed 255 characters.");
            return;
        }
        try {
            await BlogService.setCoverImageAlt(websiteId, selectedBlogId, coverImageAlt);
            toast.success('Alt updated successfully.');
            altModalClose();
            fetchBlogs();
        } catch (error) {
            toast.error(error.message || 'Failed to update alt.');
        }
    };

    const altModalClose = () => {
        setSelectedBlogId(null);
        setCoverImageAlt("");
        setShowImageAltModal(false); // Ensure modal closes
    }; 

    const dateModalClose = () => {
        setSelectedBlogId(null);
        setSelectedDate("");
        setShowDateModal(false); // Ensure modal closes
    };    

    const convertUTCToLocal = (utcDateString) => {
        if (!utcDateString) return 'N/A'; // Handle null or undefined cases
        const localDate = new Date(utcDateString);
        return localDate.toLocaleString(); // Formats the date and time to the local timezone
    };
    
    const handleSetSchema=(blog)=>{
        setBlog(blog);
        setShowSchemaModal(true);
    };

    const handleSetAlt=(blog)=>{
        setSelectedBlogId(blog.id);
        setCoverImageAlt(blog.coverImageAlt);
        setShowImageAltModal(true);
    };

    const handleSetSlug=(blog)=>{
        setSelectedBlogId(blog.id);
        setSlug(blog.formattedTitle);
        setShowSlugModal(true);
    };

    const handlSlug = async () => {
        if (!slug) {
            toast.error("Slug must not be null or empty.");
            return;
        }

        try {
            await BlogService.setSlug(websiteId, selectedBlogId, slug);
            toast.success('Slug updated successfully.');
            slugModalClose();
            fetchBlogs();
        } catch (error) {
            toast.error(error.message || 'Failed to update Slug.');
        }
    };

    const slugModalClose = () => {
        setSelectedBlogId(null);
        setSlug("");
        setShowSlugModal(false); // Ensure modal closes
    }; 

    return (
        <div className="table-responsive">
            <div className="mb-3">
                <strong>Total Blogs: {totalBlogs}</strong>
            </div>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            <th>#</th>
                            <th>Title</th>
                            <th>Author</th>
                            <th>Keywords</th>
                            <th>Created At</th>
                            <th>Published Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {blogs.map((blog, index) => (
                            <tr key={blog.id}>
                                <td>{index + 1 + currentPage * size}</td>
                                <td>{blog.title}</td>
                                <td>{blog.author}</td>
                                <td>{blog.keywords}</td>
                                <td>{convertUTCToLocal(blog.createdAt)}</td>
                                <td>{convertUTCToLocal(blog.publishedDate)}</td>
                                <td>
                                    <span className={`badge ${
                                        blog.status === 'APPROVED' ? 'bg-success' :
                                        blog.status === 'REJECTED' ? 'bg-danger' :
                                        'bg-warning'
                                    }`}>
                                        {blog.status}
                                    </span>
                                </td>
                                <td>
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-light btn-sm dropdown-toggle"
                                            type="button"
                                            id={`dropdownMenuButton${blog.id}`}
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            <i className="fa fa-ellipsis"></i>
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton${blog.id}`}>
                                            <li>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setSelectedBlog(blog);
                                                    }}
                                                >
                                                    View & Edit
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        handleSetSchema(blog);
                                                    }}
                                                >
                                                    Set Schema
                                                </button>
                                            </li>
                                            <li>
                                                <button className="dropdown-item" onClick={() => {
                                                    setSelectedBlogId(blog.id);
                                                    setShowDateModal(true);
                                                }}>
                                                    Change Published Date
                                                </button>
                                            </li>
                                            <li>
                                                <button className="dropdown-item" onClick={() => {
                                                    handleSetAlt(blog);
                                                }}>
                                                    Cover Image Alt
                                                </button>
                                            </li>
                                            <li>
                                                <button className="dropdown-item" onClick={() => {
                                                    handleSetSlug(blog);
                                                }}>
                                                    Update Slug
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    className="dropdown-item text-danger"
                                                    disabled={loginUser.role!=='ADMIN'}
                                                    onClick={() => {
                                                        handleDelete(blog.id);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {totalPages > 1 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
                />
            )}

            {showSchemaModal && (
                <UpdateSchemaModal
                    blog={blog}
                    selectedWebsite={websiteId}
                    onClose={() => {setShowSchemaModal(false); fetchBlogs();}}
                />
            )}
            {showDateModal && (
                <div className="modal show d-block">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Select Published Date</h5>
                            <button className="btn-close" onClick={dateModalClose}></button>
                        </div>
                        <div className="modal-body">
                            <input className="form-control" type="datetime-local" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={handleChangePublishedDate}>Submit</button>
                            <button className="btn btn-secondary" onClick={dateModalClose}>Cancel</button>
                        </div>
                    </div>
                </div>
                </div>
            )}
            {showImageAltModal && (
                <div className="modal show d-block">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Set Cover Image Alt</h5>
                            <button className="btn-close" onClick={altModalClose}></button>
                        </div>
                        <div className="modal-body">
                            <textarea className="form-control" value={coverImageAlt} onChange={(e) => setCoverImageAlt(e.target.value)} />
                            <span className="text-muted">250 character limit</span>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={handlCoverImageAlt}>Update</button>
                            <button className="btn btn-secondary" onClick={altModalClose}>Cancel</button>
                        </div>
                    </div>
                </div>
                </div>
            )}
            {showSlugModal && (
                <div className="modal show d-block">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Update Slug</h5>
                            <button className="btn-close" onClick={slugModalClose}></button>
                        </div>
                        <div className="modal-body">
                            <textarea className="form-control" value={slug} onChange={(e) => setSlug(e.target.value)} />
                            <span className="text-muted">Must not be null or empty</span>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={handlSlug}>Update</button>
                            <button className="btn btn-secondary" onClick={slugModalClose}>Cancel</button>
                        </div>
                    </div>
                </div>
                </div>
            )}
        </div>
    );
};

export default BlogTable;
